/**
 * The app's one decision: which platform to read from, and what to draw while
 * that answer is still arriving.
 *
 * In production the session comes from the shell's handshake and the client
 * reads `/v1/me/*`. In development, and in any build that sets
 * `VITE_USE_MOCK`, a mock platform answers the same shapes so the screen can
 * be judged before a browser can reach the API at all.
 */

import { useEffect, useMemo, useState } from "react";
import { MapScreen } from "./map/MapScreen";
import { DevBar } from "./DevBar";
import { createClient, type PlayClient } from "./data/client";
import { createMockClient, MOCK_COPY, type Moment } from "./data/mock";
import { connectShell, reportBadge, requestClose } from "./data/session";
import type { MeProgress, WidgetConfig, WidgetTemplate } from "./data/contract";
import type { ReceiptContent } from "./map/Receipt";

/**
 * When to answer from the mock rather than the platform.
 *
 * Not simply "in development": an app that mocks whenever it is being
 * developed is an app whose shell handshake cannot be exercised until
 * production, which is the last place to discover that the two sides disagree.
 * So the rule is about the shell, not the build. Standing alone in a tab there
 * is nobody to hand over a token, so development mocks; inside a frame there
 * is, so it speaks the protocol and reads the real platform. `VITE_USE_MOCK`
 * forces the mock either way, for a preview build with no platform behind it.
 */
const IN_FRAME = window.parent !== window;
const USE_MOCK = import.meta.env.VITE_USE_MOCK === "true" || (import.meta.env.DEV && !IN_FRAME);

interface Loaded {
  config: WidgetConfig;
  progress: MeProgress;
}

/**
 * What the receipt says at each moment. Fresh shows the prize before the route,
 * because day one has no history to report; the summit reports the season
 * closed; mid season reports a bonus that already landed. Every one of them
 * states that nothing was claimed, which is the point.
 */
function receiptFor(moment: Moment, showCoins: boolean): ReceiptContent | null {
  if (moment === "fresh") {
    return {
      eyebrow: "Season 1 begins",
      assetKey: "kit",
      title: "The Grand kit waits at the summit",
      subtitle: "Every quest on the route pays toward it",
      amount: null,
      fine: "Progress comes from what you do in your own work. This window only keeps the score.",
      button: "Show me the route",
    };
  }
  if (moment === "summit") {
    return {
      eyebrow: "Season 1 complete",
      assetKey: "kit",
      title: "The Grand kit is yours",
      subtitle: "Every station on the route is lit",
      amount: null,
      fine: showCoins ? "Season 2 opens soon. Your coins stay with you." : "Season 2 opens soon.",
      button: "See the route",
    };
  }
  if (!showCoins) return null;
  return {
    eyebrow: "Earned while you were working",
    assetKey: "coin",
    title: "Relay bonus",
    subtitle: "Tier 2 of the season track",
    amount: "+120 coins",
    fine: "Already added to your balance. There was nothing to claim.",
    button: "Got it",
  };
}

export function App() {
  const [template, setTemplate] = useState<WidgetTemplate>("dark");
  const [moment, setMoment] = useState<Moment>("mid");
  const [store, setStore] = useState(true);
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  const client: Promise<PlayClient> = useMemo(async () => {
    if (USE_MOCK) return createMockClient({ moment, template, surfaces: { store } });
    return createClient((await connectShell()).session);
  }, [moment, template, store]);

  useEffect(() => {
    let live = true;
    setError(null);
    (async () => {
      try {
        const api = await client;
        const [config, progress] = await Promise.all([api.widgetConfig(), api.progress()]);
        if (live) setData({ config, progress });
      } catch (cause) {
        if (live) setError(cause instanceof Error ? cause.message : "Something went wrong.");
      }
    })();
    return () => {
      live = false;
    };
  }, [client]);

  // Escape belongs to the shell: it owns the frame, the scrim and the close
  // control, so the app asks rather than pretending it can dismiss itself. A
  // station card takes the key first, and stops it reaching here.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (document.querySelector(".station.on")) return;
      requestClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // While the app is open it knows the dot's state better than the shell's
  // poller does, so it says so. Reading the badge never clears it; the grants
  // read is what clears, which is why this reports rather than decides.
  useEffect(() => {
    if (!data) return;
    let live = true;
    (async () => {
      try {
        const badge = await (await client).badge();
        if (live) reportBadge(badge.unacknowledged);
      } catch {
        // A badge that cannot be read is not worth an error state: the shell
        // keeps whatever it last drew, which is the safe direction.
      }
    })();
    return () => {
      live = false;
    };
  }, [data, client]);

  if (error) {
    return (
      <div className="frame" data-template={template} data-standalone={window.parent === window}>
        <div className="state">
          <div className="state-title">This window could not load</div>
          <p className="state-body">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="frame" data-template={template} data-standalone={window.parent === window}>
        <div className="state">
          <div className="spinner" aria-label="Loading" />
        </div>
      </div>
    );
  }

  return (
    <>
      <MapScreen
        config={data.config}
        progress={data.progress}
        copy={MOCK_COPY}
        receipt={receiptFor(moment, data.config.surfaces.store)}
      />
      {import.meta.env.DEV && (
        <DevBar
          template={template}
          moment={moment}
          store={store}
          onTemplate={setTemplate}
          onMoment={setMoment}
          onStore={setStore}
        />
      )}
    </>
  );
}
